import { useEffect, useState } from 'react'
import { utcToZonedTime } from 'date-fns-tz'
import endOfDay from 'date-fns/endOfDay'
import startOfDay from 'date-fns/startOfDay'
import URLS from './product-prefix'
import {
  BOTTLE_CAPACITY_LITERS,
  capacityNames,
  setLangs,
  SITES_INFO,
} from './consts'

export const getLang = (domain) => {
  const site = SITES_INFO[domain] || SITES_INFO['vinissimus.com']
  return site.lang
}

export function getShopCountryHeaders(info) {
  const domain = window.location.hostname.replace(/www\.|stage\./, '')
  const { shop, country } =
    info || SITES_INFO[domain] || SITES_INFO['vinissimus.com']
  return { 'x-shop': shop, 'x-country': country }
}

export function getDomain(domain, withLang = true) {
  let url = new URL(window.location.href)
  const lang = withLang ? `/${getLang(domain)}` : ''
  if (url.host.includes('www.')) {
    return `www.${domain}${lang}`
  }
  if (url.host.includes('localhost')) {
    return `stage.${domain}${lang}`
  }

  let sub = url.host.split('.')[0]
  return `${sub}.${domain}${lang}`
}

export function getHost(domain) {
  let url = new URL(window.location.href)
  if (url.host.includes('www.')) {
    return `www.${domain}`
  }
  if (url.host.includes('localhost')) {
    return `stage.${domain}`
  }

  let sub = url.host.split('.')[0]
  return `${sub}.${domain}`
}

export function sleep(ms) {
  return new Promise((resolve, reject) => {
    setTimeout(function () {
      resolve()
    }, ms)
  })
}

export function rsplit(str, delimiter, limit) {
  delimiter = str.split(delimiter)
  return limit
    ? [
        delimiter.splice(0, delimiter.length - limit).join('/'),
        ...delimiter.splice(-limit),
      ]
    : delimiter
}

export function usePromise(callback, trigger = undefined) {
  if (trigger === undefined) trigger = [callback]

  const [data, setData] = useState()
  const [status, setStatus] = useState('loading')

  async function load() {
    try {
      const data = await callback()
      setData(data)
      setStatus('success')
    } catch (e) {
      setStatus('failed')
    }
  }

  useEffect(() => {
    setStatus('loading')
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, trigger)

  return { data, status, setData }
}

export function toISOStringWithTimezone(date) {
  const d = utcToZonedTime(new Date(date), 'Europe/Madrid')
  const tzoffset = d.getTimezoneOffset() * 60000 //offset in milliseconds
  return new Date(d.getTime() - tzoffset).toISOString().slice(0, -1)
}

export function formatStartEnd(start, end) {
  const s = startOfDay(new Date(start))
  const e = endOfDay(new Date(end))
  return [toISOStringWithTimezone(s), toISOStringWithTimezone(e)]
}

export function isSameDay(d1, d2) {
  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  )
}

export function getOrSet(obj, key, value) {
  if (obj[key] == null) return (obj[key] = value)
  return obj[key]
}

export function useClipboard(key) {
  return useState(() => {
    const value = sessionStorage.getItem(key)
    if (!value) return
    return JSON.parse(value)
  }, [key])
}

export const allowToMoveOnlyInAccordionHeader = {
  moves: (el, source, handle) =>
    [...document.querySelectorAll('.accordion-card header')].some((n) =>
      n.contains(handle)
    ),
}

export function arrayMove(array, fromIndex, toIndex) {
  const arr = [...array]
  const element = arr[fromIndex]
  arr.splice(fromIndex, 1)
  arr.splice(toIndex, 0, element)
  return arr
}

export function getFiltersByPath(path) {
  try {
    return JSON.parse(localStorage.getItem(`filters:${path}`) || '[]')
  } catch (e) {
    return []
  }
}

// First time is setting to all languages the same content
// But then you can edit a particular language content
export function setMultiLangContent(content, lang, obj = {}) {
  if (Object.values(obj).filter((v) => v).length > 0) {
    return { ...obj, [lang]: content }
  }
  return setLangs(content)
}

export function getCapacityText(capacityKey) {
  if (capacityNames[capacityKey]) return capacityNames[capacityKey]

  const liters = BOTTLE_CAPACITY_LITERS[capacityKey]

  if (!liters) return ''

  const value = liters.toLocaleString('en-EN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    minimumIntegerDigits: 1,
  })

  return `${value} L`
}

export function getDomainFromShop(shop) {
  for (const domain in SITES_INFO) {
    if (SITES_INFO[domain].shop === shop) {
      return getDomain(domain, false)
    }
  }
  return getDomain('vinissimus.com', false)
}

export function getProductMask({ id, type = '' } = {}, lang) {
  const isGeneric = type.startsWith('/pk') || type.startsWith('/co')
  const maskItem = isGeneric ? URLS.generic : URLS[type] || URLS.default

  return `${maskItem[lang] || maskItem.default}/${id}/`
}

export function getCellarMask(id, lang) {
  const maskItem = {
    de: '/weingut',
    en: '/winery',
    es: '/bodega',
    fr: '/domaine',
    it: '/cantina',
  }

  return `${maskItem[lang]}/${id}/`
}

export function hexToRgb(hex) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return [0, 0, 0]
  return [
    parseInt(result[1], 16), // R
    parseInt(result[2], 16), // G
    parseInt(result[3], 16), // B
  ]
}

function formatStock(stock) {
  if ([null, undefined].includes(stock)) return 0

  return [-1, 9999].includes(stock) ? Infinity : stock
}

export function getTotalStock({
  availability = [],
  caracteristicas = {},
} = {}) {
  if (caracteristicas?.avanzada) return Infinity
  if (availability.length === 0) return 0

  return Math.max(...availability.map((a) => formatStock(a.stock)))
}

/**
 * Check if is dto offer
 */
export function isDtoOffer({ precio, oferta } = {}) {
  const { precio_oferta, descuento_especial, mUnit, uni_compra } = oferta || {}

  return (
    precio > 0 &&
    precio_oferta > 0 &&
    !descuento_especial &&
    !mUnit &&
    !uni_compra
  )
}

/**
 * Check if is MxN Promo
 */
export function isPromoMXN({ oferta } = {}) {
  const { uni_promo, uni_compra } = oferta || {}
  const mUnit = uni_promo ? uni_compra + uni_promo : undefined

  return typeof mUnit === 'number' && typeof uni_compra === 'number'
}

/**
 * Check if is xN Promo
 */
export function isPromoXN({ oferta } = {}) {
  const { uni_promo, uni_compra } = oferta || {}
  const mUnit = uni_promo ? uni_compra + uni_promo : undefined

  return typeof mUnit !== 'number' && typeof uni_compra === 'number'
}

/**
 * Get the offer price depending the quantity.
 *
 * Only is returning the price if there are some offer.
 * Otherwise is returning undefined.
 *
 * @param {number} realQuantity
 * @param {object} product - Product Context Consumer object
 * @returns {number|undefined}
 */
export function getOfferPrice(realQuantity, option, displayUnitary = false) {
  const { precio, oferta } = option || {}
  const { precio_oferta, uni_compra, uni_promo } = oferta || {}
  const mUnit = uni_promo ? uni_compra + uni_promo : undefined

  if (isDtoOffer(option)) {
    return precio_oferta * realQuantity
  }

  if (isPromoXN(option)) {
    return realQuantity >= uni_compra ? precio_oferta * realQuantity : undefined
  }

  if (isPromoMXN(option)) {
    if (realQuantity < mUnit) return undefined
    const totalPrice =
      precio *
      (parseInt(realQuantity / mUnit, 10) * uni_compra + (realQuantity % mUnit))

    return displayUnitary ? totalPrice / realQuantity : totalPrice
  }

  return undefined
}

export function encodeAbsoluteLink(linkURL) {
  try {
    return new URL(linkURL).href
  } catch (e) {
    return linkURL
  }
}

export function getProductName(originalName, vintage, capacity) {
  let name = originalName
  let isStandardCapacity = capacity === '75'

  if (vintage) name += ` ${vintage}`

  if (!isStandardCapacity) {
    const capacityText = getCapacityText(capacity)
    const capacityWithoutSpaces = capacityText.replace(/\s/g, '')
    if (
      capacityText &&
      !name.endsWith(capacityText) &&
      !name.endsWith(capacityWithoutSpaces)
    ) {
      name += ` (${capacityText})`
    }
  }

  return name
}

export function getStoreFromCountry(country) {
  if (country === 'uk') return 'uk'
  if (['it', 'ch'].includes(country)) return 'italvinus'
  return 'vinissimus'
}
